














































































































































































































import {
    AlertBanner,
    ConfirmModal,
    CopyToClipboardButton,
    FormBlock,
    JsonParser,
    SvgImage,
    TwButton,
} from '@/app/components';
import { useAxios, useFilters, useSchedule } from '@/app/composable';
import { requiredValidator } from '@/app/validators';
import { KafkaAPI } from '@/modules/data-checkin/api';
import { RefreshIcon } from '@vue-hero-icons/outline';
import { computed, defineComponent, reactive, ref, watch } from '@vue/composition-api';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { TrinityRingsSpinner } from 'epic-spinners';
import * as R from 'ramda';
import { extend, ValidationObserver } from 'vee-validate';
import { ProcessingOptions, RetrievalSettings, SampleUpload } from '../../components';
import { ProcessingOptions as ProcOptions } from '../../constants/processing-options.constants';

extend('required', requiredValidator);

dayjs.extend(minMax);
dayjs.extend(utc);

const { formatBytes } = useFilters();

export default defineComponent({
    name: 'KafkaConfiguration',
    model: {
        prop: 'configuration',
    },
    props: {
        configuration: {
            type: Object,
            required: true,
        },
        sample: {
            type: [Object, Array],
            required: false,
        },
        files: {
            type: Object,
            required: true,
        },
        activeTab: {
            type: Number,
            required: true,
        },
        jobId: {
            type: Number,
            required: true,
        },
        completed: {
            type: Boolean,
            default: true,
        },
        running: {
            type: Boolean,
            default: true,
        },
        initializing: {
            type: Boolean,
            default: false,
        },
        isDeprecated: {
            type: Boolean,
            default: false,
        },
        workflowId: {
            type: String,
            required: true,
        },
        jobCreatedById: {
            type: Number,
        },
        pipelineFinalized: {
            type: Boolean,
            required: true,
        },
        stepId: {
            type: Number,
            required: true,
        },
        isFinalized: {
            type: Boolean,
            default: false,
        },
        loadingFinalization: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        FormBlock,
        JsonParser,
        ValidationObserver,
        TwButton,
        ConfirmModal,
        TrinityRingsSpinner,
        SvgImage,
        CopyToClipboardButton,
        SampleUpload,
        RefreshIcon,
        ProcessingOptions,
        RetrievalSettings,
        AlertBanner,
    },
    setup(props, { root, emit }) {
        const isSampleArray = computed(() => R.is(Array, props.sample));
        const { exec } = useAxios(true);
        const schedules = ref([]);
        const sampleFile = computed(() => props.files.sample);
        const sampleRef = ref<any>(null);
        const dateYesterday = computed(() => {
            const date = new Date();
            date.setDate(date.getDate() - 1);
            return date;
        });
        const kafkaValidationRef = ref<any>(null);
        const showResetPasswordModal = ref<boolean>(false);
        const resettingPassword = ref<boolean>(false);
        const completedStep = computed(() => props.completed);
        const invalidFormat = ref<boolean>(false);
        const initializationFailed = ref<boolean>(false);

        const { validStreamingSchedules, validEndDate, endDateError, streamingSchedulesError } = useSchedule();

        const tomorrow = computed(() => {
            const date = new Date();
            return date.setDate(date.getDate() + 1);
        });

        const resetButton = computed(() => {
            if (resettingPassword.value) return 'Resetting Password...';
            return 'Reset Password';
        });

        const connectionDetails = reactive({
            url: null,
            topic: null,
            securityProtocol: null,
            saslMechanism: null,
            username: '',
            password: null,
        });

        const updateSchedules = (updatedSchedules: any) => {
            schedules.value = updatedSchedules;
        };

        if (!props.initializing && !props.configuration.connectionDetails.username) {
            emit('initialize-step');
        }

        watch(
            () => props.configuration.connectionDetails,
            (details) => {
                if (details) {
                    initializationFailed.value = false;
                    if (details.username) {
                        let brokers = details.url.split(',').map((broker: string) => broker.trim());
                        brokers = brokers.join(', ');
                        connectionDetails.url = brokers;
                        connectionDetails.topic = details.topic;
                        connectionDetails.securityProtocol = details.securityProtocol;
                        connectionDetails.saslMechanism = details.saslMechanism;
                        connectionDetails.username = details.username;
                        connectionDetails.password = details.password;
                        if (props.configuration.connectionDetails.password) {
                            props.configuration.connectionDetails.password = null; // eslint-disable-line no-param-reassign
                            emit('save-step');
                        }
                    } else {
                        initializationFailed.value = true;
                    }
                }
            },
            { immediate: true },
        );

        const validateAndProceed = async () => {
            if (!kafkaValidationRef.value) return;

            const valid = await kafkaValidationRef.value.validate();
            if (!valid) return;

            if (!props.pipelineFinalized) {
                if (props.configuration.processing === ProcOptions.TimeBased) {
                    const schedulesAreValid = validStreamingSchedules(schedules.value);
                    if (!schedulesAreValid) {
                        (root as any).$toastr.e(streamingSchedulesError.description, streamingSchedulesError.title);
                        return;
                    }
                } else {
                    const endDateIsValid = validEndDate(props.configuration.retrieval.endDate);
                    if (!endDateIsValid) {
                        (root as any).$toastr.e(endDateError.description, endDateError.title);
                        return;
                    }
                }
            }

            emit('next-tab');
        };

        const validate = async () => {
            return kafkaValidationRef.value.validate();
        };

        const confirmResetPassword = () => {
            showResetPasswordModal.value = true;
        };

        const resetPassword = async () => {
            showResetPasswordModal.value = false;
            resettingPassword.value = true;
            exec(KafkaAPI.resetPassword(connectionDetails.username, undefined, props.jobId))
                .then((res) => {
                    if (res) connectionDetails.password = res.data.password;
                    resettingPassword.value = false;
                    (root as any).$toastr.s('Kafka password has been reset!', 'Success');
                })
                .catch(() => {
                    (root as any).$toastr.e('Failed to reset kafka password', 'Failed');
                    resettingPassword.value = false;
                });
        };

        const invalidNoOfSchedules = computed(
            () => props.configuration.processing === ProcOptions.TimeBased && !schedules.value.length,
        );

        const displaySampleIsArrayBanner = computed(
            () => isSampleArray.value && !props.isFinalized && !props.loadingFinalization,
        );

        return {
            tomorrow,
            resetButton,
            showResetPasswordModal,
            connectionDetails,
            dateYesterday,
            kafkaValidationRef,
            formatBytes,
            sampleFile,
            sampleRef,
            validate,
            validateAndProceed,
            confirmResetPassword,
            resetPassword,
            resettingPassword,
            invalidFormat,
            initializationFailed,
            completedStep,
            updateSchedules,
            invalidNoOfSchedules,
            schedules,
            isSampleArray,
            displaySampleIsArrayBanner,
            ProcOptions,
        };
    },
});
